/**
 * Stem Extraction Service
 * Handles audio stem separation operations
 */

import { AudioPodClient } from '../client/AudioPodClient';
import {
  Job,
  StemExtractionRequest,
  StemExtractionResult
} from '../types';

export class StemExtractionService {
  constructor(private client: AudioPodClient) {}

  /**
   * Extract stems from audio (vocals, drums, bass, other instruments)
   */
  async extractStems(request: StemExtractionRequest): Promise<StemExtractionResult> {
    const {
      audioFile,
      url,
      stemTypes = ['vocals', 'drums', 'bass', 'other'],
      modelName = 'htdemucs',
      twoStemsMode,
      waitForCompletion = false,
      timeout = 600000
    } = request;

    // Validate inputs
    if (!audioFile && !url) {
      throw new Error('Either audioFile or url must be provided');
    }

    if (audioFile && url) {
      throw new Error('Cannot provide both audioFile and url');
    }

    if (!stemTypes || stemTypes.length === 0) {
      throw new Error('At least one stem type must be specified');
    }

    const validModels = ['htdemucs', 'htdemucs_6s'];
    if (!validModels.includes(modelName)) {
      throw new Error(`Invalid model name. Must be one of: ${validModels.join(', ')}`);
    }

    // Validate stem types based on model
    const validStems = modelName === 'htdemucs_6s'
      ? ['vocals', 'drums', 'bass', 'other', 'piano', 'guitar']
      : ['vocals', 'drums', 'bass', 'other'];

    for (const stemType of stemTypes) {
      if (!validStems.includes(stemType)) {
        throw new Error(`Invalid stem type '${stemType}' for model ${modelName}. Valid types: ${validStems.join(', ')}`);
      }
    }

    let job: Job;

    if (url) {
      // URL-based extraction
      const formData: Record<string, any> = {
        url,
        stem_types: JSON.stringify(stemTypes),
        model_name: modelName
      };

      if (twoStemsMode) {
        formData.two_stems_mode = twoStemsMode;
      }

      job = await this.client.post<Job>('/stem-extraction/extract', formData);
    } else {
      // File-based extraction
      let fileData: File | Blob;

      if (typeof audioFile === 'string') {
        const fs = await import('fs');
        const path = await import('path');

        if (!fs.existsSync(audioFile)) {
          throw new Error(`Audio file not found: ${audioFile}`);
        }

        const fileBuffer = fs.readFileSync(audioFile);
        const fileName = path.basename(audioFile);
        fileData = new Blob([fileBuffer], { type: 'audio/*' });
        (fileData as any).name = fileName;
      } else {
        fileData = audioFile!;
      }

      const formData: Record<string, any> = {
        stem_types: JSON.stringify(stemTypes),
        model_name: modelName
      };

      if (twoStemsMode) {
        formData.two_stems_mode = twoStemsMode;
      }

      job = await this.client.uploadFile<Job>(
        '/stem-extraction/extract',
        fileData,
        formData
      );
    }

    if (waitForCompletion) {
      const result = await this.client.waitForJobCompletion(job.id, timeout);
      return {
        job: { ...job, ...result },
        stemPaths: result.stem_paths || result.stemPaths,
        downloadUrls: result.download_urls || result.downloadUrls,
        qualityScores: result.quality_scores || result.qualityScores
      };
    }

    return {
      job,
      stemPaths: undefined,
      downloadUrls: undefined,
      qualityScores: undefined
    };
  }

  /**
   * Get stem extraction job status
   */
  async getJobStatus(jobId: number): Promise<StemExtractionResult> {
    const result = await this.client.get<any>(`/stem-extraction/status/${jobId}`);

    return {
      job: result,
      stemPaths: result.stem_paths || result.stemPaths,
      downloadUrls: result.download_urls || result.downloadUrls,
      qualityScores: result.quality_scores || result.qualityScores
    };
  }

  /**
   * List stem extraction jobs
   */
  async listJobs(options: {
    skip?: number;
    limit?: number;
  } = {}): Promise<StemExtractionResult[]> {
    const { skip = 0, limit = 50 } = options;

    const jobs = await this.client.get<any[]>('/stem-extraction/jobs', {
      skip,
      limit
    });

    return jobs.map(job => ({
      job,
      stemPaths: job.stem_paths || job.stemPaths,
      downloadUrls: job.download_urls || job.downloadUrls,
      qualityScores: job.quality_scores || job.qualityScores
    }));
  }

  /**
   * Delete stem extraction job
   */
  async deleteJob(jobId: number): Promise<void> {
    await this.client.delete(`/stem-extraction/jobs/${jobId}`);
  }
}
